import React, { useState, useEffect } from "react";
import { Card, Col, Row, Button, Alert, Badge, Modal, Spinner, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPhone, 
  faCheckCircle, 
  faExclamationTriangle,
  faRocket,
  faSms,
  faUserPlus,
  faCalendarCheck,
  faBullhorn,
  faInfoCircle,
  faCrown,
  faTrash,
  faPhoneAlt,
  faPaperPlane,
  faBrain,
  faNetworkWired,
  faCog,
  faShieldAlt,
  faUserShield
} from "@fortawesome/free-solid-svg-icons";

interface PhoneNumberInfo {
  phone_number: string | null;
  status: string;
  assigned_at: string;
  subscription_tier: string;
  eligible: boolean;
}

interface TestMessageResult {
  success: boolean;
  message?: string;
  error?: string;
}



const PhoneNumberManagement: React.FC = () => {
  // Add AI-Forward CSS animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes ai-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
      
      .ai-pulse {
        animation: ai-pulse 2s infinite;
      }
      
      .ai-hover-lift {
        transition: all 0.3s ease;
      }
      
      .ai-hover-lift:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 212, 255, 0.2) !important;
      }
      
      .ai-glass-primary, .ai-glass-elevated, .ai-glass-warning, .ai-glass-success {
        transition: all 0.3s ease;
      }
      
      .ai-glass-primary:hover, .ai-glass-elevated:hover, .ai-glass-warning:hover, .ai-glass-success:hover {
        transform: translateY(-1px);
        box-shadow: 0 12px 40px rgba(0, 212, 255, 0.15) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [phoneInfo, setPhoneInfo] = useState<PhoneNumberInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [assigningNumber, setAssigningNumber] = useState(false);
  const [releasingNumber, setReleasingNumber] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState<TestMessageResult | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(false);

  // Fetch phone number information
  const fetchPhoneInfo = async () => {
    try {
      console.log('🔍 [PHONE MANAGEMENT] Fetching phone info...');
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('❌ [PHONE MANAGEMENT] No auth token found');
        return;
      }

      console.log('🔑 [PHONE MANAGEMENT] Using auth token (first 20 chars):', token.substring(0, 20) + '...');

      // Use absolute URL to bypass proxy issues
      const apiUrl = 'http://localhost:8080/api/platform-messaging/my-phone-number';
      console.log('🌐 [PHONE MANAGEMENT] Making request to:', apiUrl);

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 [PHONE MANAGEMENT] API Response status:', response.status);
      console.log('📡 [PHONE MANAGEMENT] API Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const result = await response.json();
        console.log('📋 [PHONE MANAGEMENT] Full API Response:', JSON.stringify(result, null, 2));
        console.log('📋 [PHONE MANAGEMENT] Result.data:', result.data);
        console.log('📋 [PHONE MANAGEMENT] Result.data type:', typeof result.data);
        setPhoneInfo(result.data);
      } else {
        const errorText = await response.text();
        console.error('❌ [PHONE MANAGEMENT] API Error:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ [PHONE MANAGEMENT] Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Request phone number assignment
  const requestPhoneNumber = async () => {
    setAssigningNumber(true);
    try {
      console.log('📞 [REQUEST PHONE] Starting phone number request...');
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('❌ [REQUEST PHONE] No auth token found');
        return;
      }

      const apiUrl = 'http://localhost:8080/api/platform-messaging/ensure-phone-number';
      console.log('🌐 [REQUEST PHONE] Making request to:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 [REQUEST PHONE] API Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📋 [REQUEST PHONE] API Response:', JSON.stringify(result, null, 2));
        
        if (result.success) {
          console.log('✅ [REQUEST PHONE] Phone number assigned successfully!');
          await fetchPhoneInfo(); // Refresh info to show the new number
          setShowSuccessModal(true); // Show success modal
        } else {
          console.error('❌ [REQUEST PHONE] Assignment failed:', result.error);
          alert(result.error || 'Failed to assign phone number');
        }
      } else {
        const errorText = await response.text();
        console.error('❌ [REQUEST PHONE] HTTP Error:', response.status, errorText);
        alert('Failed to request phone number');
      }
    } catch (error) {
      console.error('❌ [REQUEST PHONE] Network error:', error);
      alert('Error requesting phone number: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setAssigningNumber(false);
    }
  };

  // Confirm and actually release the number
  const confirmReleasePhoneNumber = async () => {
    setShowConfirmModal(false);
    setReleasingNumber(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch('/api/twilio/release-number', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await fetchPhoneInfo(); // Refresh info
        } else {
          alert(result.error || 'Failed to release phone number');
        }
      } else {
        alert('Failed to release phone number');
      }
    } catch (error) {
      console.error('Error releasing phone number:', error);
      alert('Error releasing phone number');
    } finally {
      setReleasingNumber(false);
    }
  };

  // Send test message
  const sendTestMessage = async () => {
    if (!testMessage.trim()) {
      setTestResult({ success: false, error: 'Please enter a test message' });
      return;
    }

    setSendingTest(true);
    setTestResult(null);

    try {
      console.log('📱 [SMS TEST] Starting SMS test...');
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('❌ [SMS TEST] No auth token found');
        setTestResult({ success: false, error: 'Authentication required' });
        return;
      }

      console.log('📱 [SMS TEST] Sending to:', phoneInfo?.phone_number);
      console.log('📱 [SMS TEST] Message:', testMessage);

      // Use direct URL like other API calls
      const apiUrl = 'http://localhost:8080/api/platform-messaging/send-notification';
      console.log('🌐 [SMS TEST] Making request to:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_phone: phoneInfo?.phone_number,
          message: testMessage,
          message_type: 'test'
        }),
      });

      console.log('📡 [SMS TEST] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [SMS TEST] HTTP Error:', response.status, errorText);
        setTestResult({ 
          success: false, 
          error: `API Error: ${response.status} - ${errorText}` 
        });
        return;
      }

      const result = await response.json();
      console.log('📋 [SMS TEST] Response:', JSON.stringify(result, null, 2));
      
      if (result.success) {
        setTestResult({ 
          success: true, 
          message: 'Test message sent successfully! Your business communications are working perfectly.' 
        });
        setTestMessage('');
        
        // Auto-complete setup if test was successful and setup isn't complete
        if (!isSetupComplete) {
          setTimeout(() => {
            setIsSetupComplete(true);
            localStorage.setItem('communicationsComplete', 'true');
            
            // Trigger storage event to update progress bar
            window.dispatchEvent(new StorageEvent('storage', {
              key: 'communicationsComplete',
              newValue: 'true',
              oldValue: null,
              storageArea: localStorage
            }));
            
            console.log('✅ [COMMUNICATIONS] Auto-completed after successful test message');
          }, 2000); // Mark complete after 2 seconds
        }
      } else {
        setTestResult({ 
          success: false, 
          error: result.error || 'Failed to send test message' 
        });
      }
    } catch (error) {
      console.error('❌ [SMS TEST] Network error:', error);
      setTestResult({ 
        success: false, 
        error: 'Error sending test message: ' + (error instanceof Error ? error.message : 'Unknown error')
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleReleasePhoneNumber = async () => {
    if (!phoneInfo?.phone_number) return;
    setShowConfirmModal(true);
  };

  // Mark setup as complete when user has a phone number
  const markSetupComplete = () => {
    setIsSetupComplete(true);
    setShowSuccessModal(false);
    
    // Save completion status to localStorage so ProfileSettings can track it
    localStorage.setItem('communicationsComplete', 'true');
    
    // Trigger storage event to notify other tabs/components
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'communicationsComplete',
      newValue: 'true',
      oldValue: null,
      storageArea: localStorage
    }));
    
    console.log('✅ [COMMUNICATIONS] Setup marked as complete');
  };

  useEffect(() => {
    fetchPhoneInfo();
  }, []);

  // Check if setup is complete (has phone number and optional test sent)
  useEffect(() => {
    if (phoneInfo?.phone_number && !isSetupComplete) {
      // Auto-complete if they already have a number from previous session
      const hasExistingNumber = phoneInfo.assigned_at && 
        new Date(phoneInfo.assigned_at) < new Date(Date.now() - 60000); // Assigned more than 1 min ago
      if (hasExistingNumber) {
        setIsSetupComplete(true);
        // Also update localStorage for auto-complete scenarios
        localStorage.setItem('communicationsComplete', 'true');
      }
    }
  }, [phoneInfo, isSetupComplete]);

  // Check for previously saved completion status on component mount
  useEffect(() => {
    const savedCompletion = localStorage.getItem('communicationsComplete');
    if (savedCompletion === 'true') {
      setIsSetupComplete(true);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading communication settings...</p>
      </div>
    );
  }

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1);
      return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
    }
    return phone;
  };

  const getSubscriptionTierInfo = (tier: string): { name: string; color: string; icon: any } => {
    const tierInfo: Record<string, { name: string; color: string; icon: any }> = {
      'essential': { name: 'Essential', color: 'secondary', icon: faInfoCircle },
      'growth': { name: 'Growth', color: 'primary', icon: faUserPlus },
      'premium': { name: 'Premium', color: 'success', icon: faRocket },
      'enterprise': { name: 'Enterprise', color: 'warning', icon: faCrown },
      'business': { name: 'Business', color: 'info', icon: faBullhorn },
      // Legacy support for old naming
      'basic': { name: 'Essential', color: 'secondary', icon: faInfoCircle },
      'pro': { name: 'Growth', color: 'primary', icon: faUserPlus }
    };
    return tierInfo[tier.toLowerCase()] || tierInfo.essential;
  };

  // Show phone interface if user is eligible (not Essential tier)
  const showPhoneInterface = phoneInfo?.eligible || false;
  const hasPhoneNumber = phoneInfo?.phone_number;

  return (
    <div>
      {/* AI-Enhanced Header */}
      <div 
        className="ai-glass-primary p-4 mb-4 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)',
          backdropFilter: 'blur(15px)',
          borderRadius: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div 
              className="ai-icon-container me-3"
              style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
                animation: 'ai-pulse 2s infinite'
              }}
            >
              <FontAwesomeIcon 
                icon={faPhone} 
                size="lg" 
                className="text-white"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                }}
              />
            </div>
            <div>
              <div className="d-flex align-items-center mb-1">
                <h4 className="mb-0 fw-bold me-3" style={{ color: '#1f2937' }}>
                  AI Communications Hub
                </h4>
                <div className="ai-neural-indicator">
                  <FontAwesomeIcon 
                    icon={faNetworkWired} 
                    className="text-primary me-1"
                    style={{
                      fontSize: '0.8rem',
                      animation: 'ai-pulse 1.5s infinite'
                    }}
                  />
                  <small className="text-primary fw-semibold">Neural Network Active</small>
                </div>
              </div>
              <p className="text-muted mb-0">
                AI-powered business communications for intelligent customer engagement
              </p>
            </div>
          </div>
          
          {/* AI Status Indicators */}
          <div className="ai-status-indicators d-flex flex-column align-items-end">
            {phoneInfo?.subscription_tier && (
              <div className="d-flex align-items-center mb-2">
                <Badge 
                  className="px-3 py-2 me-2"
                  style={{
                    background: `linear-gradient(135deg, ${getSubscriptionTierInfo(phoneInfo.subscription_tier).color === 'primary' ? '#667eea' : getSubscriptionTierInfo(phoneInfo.subscription_tier).color === 'success' ? '#00d4ff' : '#f59e0b'} 0%, ${getSubscriptionTierInfo(phoneInfo.subscription_tier).color === 'primary' ? '#764ba2' : getSubscriptionTierInfo(phoneInfo.subscription_tier).color === 'success' ? '#667eea' : '#d97706'} 100%)`,
                    border: 'none',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={getSubscriptionTierInfo(phoneInfo.subscription_tier).icon} 
                    className="me-2"
                  />
                  {getSubscriptionTierInfo(phoneInfo.subscription_tier).name}
                </Badge>
              </div>
            )}
            
            {/* AI Active Status */}
            <Badge 
              className="ai-pulse mb-2 px-3 py-2"
              style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                border: 'none',
                fontSize: '0.75rem',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}
            >
              <FontAwesomeIcon icon={faBrain} className="me-1" />
              AI ACTIVE
            </Badge>
            
            {/* Setup Completion Status */}
            {showPhoneInterface && hasPhoneNumber && (
              <div className="d-flex align-items-center">
                {isSetupComplete ? (
                  <Badge 
                    className="px-3 py-2"
                    style={{
                      background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Neural Network Complete
                  </Badge>
                ) : (
                  <Badge 
                    className="ai-pulse px-3 py-2"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}
                  >
                    <FontAwesomeIcon icon={faCog} className="me-2" />
                    AI Calibrating
                  </Badge>
                )}
              </div>
            )}
            
            <small className="text-muted mt-1">Intelligence Level: Advanced</small>
          </div>
        </div>
      </div>

      {/* Debug Info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <Alert variant="info" className="mb-4">
          <small>
            <strong>Debug:</strong> Tier: {phoneInfo?.subscription_tier || 'unknown'} | 
            Eligible: {phoneInfo?.eligible ? 'Yes' : 'No'} | 
            Phone: {phoneInfo?.phone_number || 'None'}
          </small>
        </Alert>
      )}

      {/* Main Content */}
      {showPhoneInterface ? (
        hasPhoneNumber ? (
          /* Show phone management interface */
          <div>
            {/* AI-Enhanced Phone Number Display */}
            <div 
              className="ai-glass-elevated mb-4 border-0"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)',
                backdropFilter: 'blur(15px)',
                borderRadius: '16px',
                border: '1px solid rgba(0, 212, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)'
              }}
            >
              <div 
                className="ai-intelligence-header p-3"
                style={{
                  background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                  borderRadius: '16px 16px 0 0',
                  color: 'white'
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div 
                      className="ai-icon-glow me-2"
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faPhoneAlt} />
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">Neural Communication Node</h6>
                      <small style={{ opacity: 0.9 }}>AI-Enhanced Business Line</small>
                    </div>
                  </div>
                  <Badge 
                    className="ai-status-indicator"
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      fontSize: '0.7rem'
                    }}
                  >
                    <FontAwesomeIcon icon={faNetworkWired} className="me-1" />
                    ACTIVE
                  </Badge>
                </div>
              </div>
              
              <div className="ai-content p-4">
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="ai-metric-display mb-3">
                      <h3 
                        className="ai-glow-text mb-2"
                        style={{
                          background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 'bold',
                          fontSize: '1.8rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faPhoneAlt} className="me-2" style={{ color: '#00d4ff' }} />
                        {formatPhoneNumber(phoneInfo.phone_number || '')}
                      </h3>
                      <div className="ai-confidence-indicator d-flex align-items-center">
                        <div 
                          className="me-2"
                          style={{
                            width: '8px',
                            height: '8px',
                            background: '#00d4ff',
                            borderRadius: '50%',
                            animation: 'ai-pulse 2s infinite'
                          }}
                        />
                        <small className="text-success fw-semibold">Network Status: Optimal</small>
                      </div>
                    </div>
                    
                    {phoneInfo.assigned_at && (
                      <div className="ai-metadata">
                        <small className="text-muted d-flex align-items-center">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                          Neural Assignment: {new Date(phoneInfo.assigned_at).toLocaleDateString()}
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col md={4} className="text-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleReleasePhoneNumber}
                      disabled={releasingNumber}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      {releasingNumber ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Disconnecting...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          Disconnect
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>

            {/* AI Setup Completion Section */}
            {!isSetupComplete && (
              <div 
                className="ai-glass-warning mb-4 border-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  boxShadow: '0 8px 32px rgba(245, 158, 11, 0.1)'
                }}
              >
                <div 
                  className="ai-intelligence-header p-3"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px 16px 0 0',
                    color: 'white'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div 
                      className="ai-icon-glow me-2"
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faCog} className="fa-spin" />
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">Neural Network Calibration</h6>
                      <small style={{ opacity: 0.9 }}>AI System Ready for Activation</small>
                    </div>
                  </div>
                </div>
                
                <div className="ai-content p-4">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="ai-activation-prompt">
                        <h6 className="mb-2 fw-bold" style={{ color: '#1f2937' }}>
                          🚀 Ready to activate neural communication pathways!
                        </h6>
                        <p className="text-muted mb-3">
                          Your AI communication node is configured. Initiate the neural network to enable 
                          intelligent SMS messaging and automated appointment confirmations.
                        </p>
                        <div className="ai-progress-indicator d-flex align-items-center">
                          <div 
                            className="me-2"
                            style={{
                              width: '6px',
                              height: '6px',
                              background: '#f59e0b',
                              borderRadius: '50%',
                              animation: 'ai-pulse 1.5s infinite'
                            }}
                          />
                          <small className="text-warning fw-semibold">Neural pathways: 95% calibrated</small>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <Button
                        onClick={markSetupComplete}
                        className="px-4 py-3 border-0"
                        style={{
                          background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                          borderRadius: '12px',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '1rem',
                          boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 212, 255, 0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 212, 255, 0.3)';
                        }}
                      >
                        <FontAwesomeIcon icon={faBrain} className="me-2" />
                        Activate Neural Network
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* AI Setup Complete Section */}
            {isSetupComplete && (
              <div 
                className="ai-glass-success mb-4 border-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 212, 255, 0.2)'
                }}
              >
                                  <div 
                    className="ai-intelligence-header p-3"
                    style={{
                      background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                      borderRadius: '16px 16px 0 0',
                      color: 'white'
                    }}
                  >
                  <div className="d-flex align-items-center">
                    <div 
                      className="ai-icon-glow me-2"
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faBrain} />
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">Neural Network Activated</h6>
                      <small style={{ opacity: 0.9 }}>AI Communications Fully Operational</small>
                    </div>
                  </div>
                </div>
                
                <div className="ai-content p-4">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="ai-success-state">
                        <h6 className="mb-2 fw-bold" style={{ color: '#667eea' }}>
                          🎉 Neural communication pathways are fully operational!
                        </h6>
                        <p className="text-muted mb-3">
                          Your AI-powered communication hub is now processing intelligent customer interactions, 
                          automated appointment confirmations, and smart outreach campaigns.
                        </p>
                        <div className="ai-operational-indicators d-flex flex-wrap gap-3">
                          <div className="d-flex align-items-center">
                            <div 
                              className="me-2"
                              style={{
                                width: '6px',
                                height: '6px',
                                background: '#00d4ff',
                                borderRadius: '50%',
                                animation: 'ai-pulse 2s infinite'
                              }}
                            />
                            <small className="text-success fw-semibold">SMS Intelligence: Active</small>
                          </div>
                          <div className="d-flex align-items-center">
                            <div 
                              className="me-2"
                              style={{
                                width: '6px',
                                height: '6px',
                                background: '#00d4ff',
                                borderRadius: '50%',
                                animation: 'ai-pulse 2s infinite'
                              }}
                            />
                            <small className="text-success fw-semibold">Neural Processing: Optimal</small>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                                              <Badge 
                          className="px-4 py-3 fs-6"
                          style={{
                            background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            color: 'white',
                            fontWeight: '600',
                            boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)'
                          }}
                        >
                        <FontAwesomeIcon icon={faNetworkWired} className="me-2" />
                        Neural Network Online
                      </Badge>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* Test Messaging */}
            <Card className="mb-4">
              <Card.Header>
                <FontAwesomeIcon icon={faSms} className="me-2" />
                Test SMS Messaging
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={8}>
                    <Form.Control
                      type="text"
                      placeholder="Enter a test message..."
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      disabled={sendingTest}
                    />
                  </Col>
                  <Col md={4}>
                    <Button
                      variant="primary"
                      onClick={sendTestMessage}
                      disabled={sendingTest || !testMessage.trim()}
                      className="w-100"
                    >
                      {sendingTest ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                          Send Test
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
                
                {testResult && (
                  <Alert 
                    variant={testResult.success ? 'success' : 'danger'} 
                    className="mt-3 mb-0"
                  >
                    <FontAwesomeIcon 
                      icon={testResult.success ? faCheckCircle : faExclamationTriangle} 
                      className="me-2" 
                    />
                    {testResult.success ? testResult.message : testResult.error}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </div>
        ) : (
          /* Show request phone number interface */
          <Card className="text-center py-5">
            <Card.Body>
              <FontAwesomeIcon 
                icon={faPhone} 
                size="3x" 
                className="text-primary mb-4" 
              />
              <h4 className="mb-3">Get Your Business Phone Number</h4>
              <p className="text-muted mb-4">
                Your <strong>{getSubscriptionTierInfo(phoneInfo?.subscription_tier || 'growth').name}</strong> subscription 
                includes a dedicated business phone number for customer communications.
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={requestPhoneNumber}
                disabled={assigningNumber}
                className="px-4"
              >
                {assigningNumber ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Assigning Number...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPhoneAlt} className="me-2" />
                    Request Phone Number
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        )
      ) : (
        /* Show upgrade message for Essential tier */
        <Card className="text-center py-5">
          <Card.Body>
            <FontAwesomeIcon 
              icon={faRocket} 
              size="3x" 
              className="text-warning mb-4" 
            />
            <h5 className="mb-2">Upgrade for Business Communications</h5>
            <p className="text-muted mb-4">
              Business phone numbers and SMS messaging are available with Growth, Premium, 
              Enterprise, and Business subscriptions.
            </p>
            <Button variant="primary" size="lg" className="px-4">
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              Upgrade Subscription
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Usage Guide */}
      <Card className="border-0 bg-light">
        <Card.Body className="p-4">
          <h6 className="mb-3">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
            How Business Communications Work
          </h6>
          
          <Row className="g-3">
            <Col md={6}>
              <div className="d-flex">
                <div className="me-3">
                  <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: '32px',
                      height: '32px',
                      background: '#e0f2fe',
                      borderRadius: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} className="text-info" size="sm" />
                  </div>
                </div>
                <div>
                  <h6 className="mb-1">Appointment Confirmations</h6>
                  <small className="text-muted">
                    Automatically send SMS confirmations and reminders to clients for scheduled appointments.
                  </small>
                </div>
              </div>
            </Col>
            
            <Col md={6}>
              <div className="d-flex">
                <div className="me-3">
                  <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: '32px',
                      height: '32px',
                      background: '#f0fdf4',
                      borderRadius: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faBullhorn} className="text-success" size="sm" />
                  </div>
                </div>
                <div>
                  <h6 className="mb-1">AI Customer Outreach</h6>
                  <small className="text-muted">
                    Send personalized, AI-generated messages for follow-ups, market updates, and lead nurturing.
                  </small>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Compliance & Fair Use Section */}
      <Card className="border-warning bg-warning-subtle mt-4">
        <Card.Body className="p-4">
          <h6 className="mb-3 text-warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            SMS Compliance & Fair Use Policy
          </h6>
          
          <Alert variant="warning" className="mb-3">
            <small>
              <strong>IMPORTANT:</strong> Business SMS communications are subject to federal regulations. 
              Failure to comply may result in significant fines and legal consequences.
            </small>
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <div className="mb-3">
                <h6 className="text-dark mb-2">
                  <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-warning" />
                  TCPA Compliance Required
                </h6>
                <ul className="list-unstyled mb-0">
                  <li className="mb-1">
                    <small>✓ Obtain written consent before sending SMS</small>
                  </li>
                  <li className="mb-1">
                    <small>✓ Provide clear opt-out instructions</small>
                  </li>
                  <li className="mb-1">
                    <small>✓ Honor opt-out requests immediately</small>
                  </li>
                  <li className="mb-1">
                    <small>✓ Maintain consent records for 4+ years</small>
                  </li>
                </ul>
              </div>
            </Col>
            
            <Col md={6}>
              <div className="mb-3">
                <h6 className="text-dark mb-2">
                  <FontAwesomeIcon icon={faUserShield} className="me-2 text-info" />
                  Best Practices
                </h6>
                <ul className="list-unstyled mb-0">
                  <li className="mb-1">
                    <small>• Business hours only (8AM-9PM local time)</small>
                  </li>
                  <li className="mb-1">
                    <small>• Include your business name in messages</small>
                  </li>
                  <li className="mb-1">
                    <small>• Limit to business-related communications</small>
                  </li>
                  <li className="mb-1">
                    <small>• Always include "Reply STOP to opt out"</small>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <div className="bg-light rounded p-3 mt-3">
            <small className="text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
              <strong>Disclaimer:</strong> KairosIQ provides tools for business communications but does not provide legal advice. 
              Consult with a qualified attorney to ensure your SMS practices comply with all applicable laws including TCPA, CAN-SPAM, and state regulations.
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Test Message Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faSms} className="me-2 text-primary" />
            Test SMS Message
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Send a test message to your assigned number: <strong>{formatPhoneNumber(phoneInfo?.phone_number || '')}</strong>
          </p>
          
          <Form.Group className="mb-3">
            <Form.Label>Test Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Enter your test message here..."
              maxLength={160}
            />
            <Form.Text className="text-muted">
              {testMessage.length}/160 characters
            </Form.Text>
          </Form.Group>

          {testResult && (
            <Alert variant={testResult.success ? 'success' : 'danger'}>
              <FontAwesomeIcon 
                icon={testResult.success ? faCheckCircle : faExclamationTriangle} 
                className="me-2" 
              />
              {testResult.success ? testResult.message : testResult.error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowTestModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={sendTestMessage}
            disabled={sendingTest || !testMessage.trim()}
          >
            {sendingTest ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSms} className="me-2" />
                Send Test Message
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Confirm Release Phone Number
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Are you sure you want to release your phone number <strong>{formatPhoneNumber(phoneInfo?.phone_number || '')}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>Warning:</strong> This action cannot be undone and will affect your customer communications. 
            You'll need to get a new phone number if you want to continue using SMS features.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmReleasePhoneNumber}
            disabled={releasingNumber}
          >
            {releasingNumber ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Releasing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Yes, Release Number
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Phone Number Assigned Successfully!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <FontAwesomeIcon 
              icon={faPhoneAlt} 
              size="3x" 
              className="text-success mb-3" 
            />
            <h4 className="text-success mb-2">
              {formatPhoneNumber(phoneInfo?.phone_number || '')}
            </h4>
            <p className="text-muted">
              Your business communication number is now active and ready to use!
            </p>
          </div>

          <div className="bg-light rounded p-3 mb-4">
            <h6 className="mb-2">
              <FontAwesomeIcon icon={faRocket} className="me-2 text-primary" />
              Next Steps
            </h6>
            <ul className="list-unstyled mb-0 text-start">
              <li className="mb-2">
                <FontAwesomeIcon icon={faSms} className="me-2 text-success" />
                Test SMS messaging below to verify everything works
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faCalendarCheck} className="me-2 text-info" />
                Start receiving automatic appointment confirmations
              </li>
              <li className="mb-0">
                <FontAwesomeIcon icon={faBullhorn} className="me-2 text-warning" />
                Use AI-powered customer outreach campaigns
              </li>
            </ul>
          </div>

          <div className="d-grid gap-2">
            <Button 
              variant="success" 
              size="lg"
              onClick={markSetupComplete}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Complete Setup
            </Button>
            <Button 
              variant="outline-primary"
              onClick={() => {
                setShowSuccessModal(false);
                setTestMessage('Hello! This is a test message from my new business number.');
              }}
            >
              <FontAwesomeIcon icon={faSms} className="me-2" />
              Send Test Message First
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PhoneNumberManagement;

// Add AI-Forward CSS animations
const aiAnimationStyles = `
  @keyframes ai-pulse {
    0%, 100% { 
      opacity: 1; 
      transform: scale(1); 
    }
    50% { 
      opacity: 0.6; 
      transform: scale(1.1); 
    }
  }

  .ai-pulse {
    animation: ai-pulse 2s infinite;
  }

  .ai-glass-elevated:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 212, 255, 0.15) !important;
    transition: all 0.3s ease;
  }

  .ai-icon-glow {
    transition: all 0.3s ease;
  }

  .ai-icon-glow:hover {
    background: rgba(255, 255, 255, 0.3) !important;
    transform: scale(1.05);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = aiAnimationStyles;
  document.head.appendChild(styleSheet);
}